/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from '@app/contexts/userContext';
import { useChatStore } from '@app/contexts/chatStoreContext';
import { idsMatch } from '@app/helpers/normalizeId';
import { chatSocketService } from '@app/services/chat-socket.service';
import {
  GET_USER_MESSAGES,
  GET_CONVERSATIONS,
} from '../../apollo/queries';
import {
  SEND_MESSAGE,
  CREATE_CONVERSATION,
} from '../../apollo/mutations';

export function useOneToOneChat() {
  const { user: authUser } = useUser();
  const {
    chatState,
    updateState,
    addOptimisticUpdate,
    removeOptimisticUpdate,
  } = useChatStore();
  const chatStateRef = useRef(chatState);
  const conversationsFetchRef = useRef({ isFetching: false, lastFetchAt: 0, retryTimeout: null });
  const loadingMessagesRef = useRef(false);

  useEffect(() => {
    chatStateRef.current = chatState;
  }, [chatState]);

  const [getConversations] = useLazyQuery(GET_CONVERSATIONS, {
    fetchPolicy: 'no-cache',
  });

  const [getConversationMessages] = useLazyQuery(GET_USER_MESSAGES, {
    fetchPolicy: 'no-cache',
  });

  const [sendMessageMutation] = useMutation(SEND_MESSAGE, {
    fetchPolicy: 'no-cache',
  });

  const [createConversationMutation] = useMutation(CREATE_CONVERSATION);

  const loadConversations = useCallback(async () => {
    if (!authUser) return;

    const now = Date.now();
    const fetchState = conversationsFetchRef.current;

    if (fetchState.isFetching || now - fetchState.lastFetchAt < 5000) {
      return;
    }

    try {
      fetchState.isFetching = true;
      fetchState.lastFetchAt = now;
      updateState({ isLoading: true, error: null });

      const result = await getConversations();
      const conversations = result.data?.userConversations || [];
      updateState({ conversations });

      // Subscribe to conversation rooms for real-time updates
      const conversationIds = conversations.map((c) => c.id).filter(Boolean);
      if (conversationIds.length > 0) {
        chatSocketService.subscribeConversations(conversationIds);
      }
    } catch (error) {
      updateState({ error: error.message });

      if (fetchState.retryTimeout) {
        clearTimeout(fetchState.retryTimeout);
      }

      fetchState.retryTimeout = setTimeout(() => {
        loadConversations();
      }, 5000);
    } finally {
      fetchState.isFetching = false;
      updateState({ isLoading: false });
    }
  }, [authUser, getConversations, updateState]);

  const loadConversationMessages = useCallback(
    async (conversation) => {
      if (!conversation || conversation.id === 0 || loadingMessagesRef.current) return;

      const friendId = conversation.friend?.id || conversation.user?.id;

      if (!friendId) {
        return;
      }

      try {
        loadingMessagesRef.current = true;
        updateState({ isLoading: true });

        const result = await getConversationMessages({
          variables: {
            friend_id: friendId,
            conversation_id: conversation?.id,
          },
        });
        const messages = result.data?.userMessages || [];
        updateState({ conversationMessages: messages });
      } catch (error) {
        updateState({ error: error.message });
      } finally {
        loadingMessagesRef.current = false;
        updateState({ isLoading: false });
      }
    },
    [getConversationMessages, updateState]
  );

  const markConversationAsRead = useCallback(
    async (conversationId) => {
      const currentCount = chatStateRef.current.conversationUnreadCounts[conversationId] || 0;

      updateState((prev) => ({
        ...prev,
        conversationUnreadCounts: {
          ...prev.conversationUnreadCounts,
          [conversationId]: 0,
        },
      }));

      if (currentCount > 0) {
        // Mark-read mutation integration can be added when backend mutation contract is finalized.
      }
    },
    [chatStateRef, updateState]
  );

  const joinConversation = useCallback(
    async (conversation) => {
      if (!conversation || !authUser) return false;

      if (idsMatch(chatStateRef.current.activeConversation?.id, conversation.id)) {
        return true;
      }

      updateState({
        activeConversation: conversation,
        activeChatUser:
          idsMatch(conversation.user?.id, authUser.id) ? conversation.friend : conversation.user,
        conversationMessages: [],
      });

      if (conversation.id !== 0) {
        await loadConversationMessages(conversation);
      }

      if (conversation.id !== 0) {
        const success = await chatSocketService.joinConversation(conversation.id);

        if (success) {
          markConversationAsRead(conversation.id);
        }

        return success;
      }

      return true;
    },
    [
      authUser,
      chatStateRef,
      loadConversationMessages,
      updateState,
      markConversationAsRead,
    ]
  );

  const leaveConversation = useCallback(() => {
    chatSocketService.leaveConversation();
    updateState({
      activeConversation: null,
      activeChatUser: null,
      conversationMessages: [],
    });
  }, [updateState]);

  const sendMessage = useCallback(
    async (messageText, media_urls = [], metadata = null) => {
      if (!chatStateRef.current.activeConversation || !messageText.trim()) return false;

      // Normalize metadata: must be a plain object or null for GraphQL JSON scalar.
      // An empty array [] or empty object {} would be rejected by the backend.
      const rawMetadata = {
        ...(metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {}),
        ...(media_urls && media_urls.length > 0 ? { media_urls } : {}),
      };
      const messageMetadata = Object.keys(rawMetadata).length > 0 ? rawMetadata : null;

      const messageUuid = uuidv4();

      if (chatStateRef.current.activeConversation.id === 0) {
        try {
          const tempMessage = {
            message: messageText,
            sender_id: authUser.id,
            receiver_id: chatStateRef.current.activeChatUser?.id,
            conversation_id: 0,
            id: messageUuid,
            uuid: messageUuid,
            createdAt: new Date().toISOString(),
            sender: authUser,
            metadata: messageMetadata,
          };

          addOptimisticUpdate(messageUuid);
          updateState((prev) => ({
            ...prev,
            conversationMessages: [...prev.conversationMessages, tempMessage],
          }));

          const result = await createConversationMutation({
            variables: {
              input: {
                friend_id: chatStateRef.current.activeChatUser?.id,
                message: messageText,
                metadata: messageMetadata,
                uuid: messageUuid,
              },
            },
          });

          if (result.data?.createConversation) {
            const newConversation = result.data.createConversation;

            removeOptimisticUpdate(messageUuid);
            updateState((prev) => ({
              ...prev,
              activeConversation: newConversation,
              conversations: [newConversation, ...prev.conversations],
              conversationMessages: newConversation.messages || [],
            }));

            await chatSocketService.joinConversation(newConversation.id);

            return true;
          }
        } catch (error) {
          removeOptimisticUpdate(messageUuid);
          updateState((prev) => ({
            ...prev,
            conversationMessages: prev.conversationMessages.filter((msg) => msg.uuid !== messageUuid),
            error: error.message,
          }));
          return false;
        }
      }

      const messageData = {
        uuid: messageUuid,
        message: messageText,
        sender_id: authUser.id,
        receiver_id: chatStateRef.current.activeChatUser?.id,
        conversation_id: chatStateRef.current.activeConversation.id,
        metadata: messageMetadata,
        media_urls: media_urls && media_urls.length > 0 ? media_urls : [],
      };

      const tempMessage = {
        ...messageData,
        id: messageUuid,
        createdAt: new Date().toISOString(),
        sender: authUser,
      };

      addOptimisticUpdate(messageUuid);
      updateState((prev) => ({
        ...prev,
        conversationMessages: [...prev.conversationMessages, tempMessage],
      }));

      if (process.env.NODE_ENV === 'development') {
        console.log('[Chat] Sending 1:1 message, uuid:', messageUuid);
      }

      chatSocketService.sendMessage(messageData);

      try {
        const result = await sendMessageMutation({ variables: { input: messageData } });
        const savedMessage = result?.data?.sendMessage;

        if (savedMessage) {
          // Replace optimistic message with the real DB message
          removeOptimisticUpdate(messageUuid);
          updateState((prev) => ({
            ...prev,
            conversationMessages: prev.conversationMessages.map((msg) =>
              msg.uuid === messageUuid ? { ...savedMessage, uuid: messageUuid } : msg
            ),
          }));

          if (process.env.NODE_ENV === 'development') {
            console.log('[Chat] Message persisted, db id:', savedMessage.id);
          }
        }

        return true;
      } catch (mutationError) {
        console.error('[Chat] Failed to persist message:', mutationError.message);
        removeOptimisticUpdate(messageUuid);
        updateState((prev) => ({
          ...prev,
          conversationMessages: prev.conversationMessages.filter(
            (msg) => msg.uuid !== messageUuid
          ),
          error: 'Failed to save message',
        }));
        return false;
      }
    },
    [
      authUser,
      chatStateRef,
      sendMessageMutation,
      createConversationMutation,
      updateState,
      addOptimisticUpdate,
      removeOptimisticUpdate,
    ]
  );

  const contactScientist = useCallback(
    async (scientist) => {
      if (!scientist || !authUser) return false;

      const existingConversation = chatStateRef.current.conversations.find(
        (conversation) =>
          idsMatch(conversation.user?.id, scientist.id) || idsMatch(conversation.friend?.id, scientist.id)
      );

      if (existingConversation) {
        return joinConversation(existingConversation);
      }

      const newConversation = {
        id: 0,
        friend: scientist,
        user: authUser,
      };

      updateState({
        activeConversation: newConversation,
        activeChatUser: scientist,
        conversationMessages: [],
      });

      return true;
    },
    [authUser, chatStateRef, joinConversation, updateState]
  );

  // Cleanup retry timeout on unmount
  useEffect(
    () => () => {
      if (conversationsFetchRef.current.retryTimeout) {
        clearTimeout(conversationsFetchRef.current.retryTimeout);
      }
    },
    []
  );

  const api = useMemo(
    () => ({
      loadConversations,
      loadConversationMessages,
      joinConversation,
      leaveConversation,
      sendMessage,
      markConversationAsRead,
      contactScientist,
      isLoading: chatState.isLoading,
    }),
    [
      loadConversations,
      loadConversationMessages,
      joinConversation,
      leaveConversation,
      sendMessage,
      markConversationAsRead,
      contactScientist,
      chatState.isLoading,
    ]
  );

  return api;
}
